import { useCallback, useMemo } from 'react';

import { PeerTypes, useShowConsumerCampaignSettingsById } from '@givebacks/api/store';
import {
  useCrowdFundingTranslations,
  useDonorTranslations,
  useFormListValidator,
} from 'core/Hooks';
import { Alert, ButtonMantine, Stack, Text, TransitionElement } from 'ui';
import { useForm } from 'ui/Form';

import { PeerCreditButtonGroup } from './PeerCreditButtonGroup';
import { PeerSelection } from './PeerSelection';
import { usePeerCreditsForm } from './usePeerCredits';

type CartPeerCredit = {
  status?: 'pledge_required' | undefined;
  credit_to: string[] | undefined;
  credit_write_in: string[] | undefined;
  peerType?: PeerTypes;
};

const ProceedToDonationButton = ({
  onProceed,
}: {
  onProceed: (cartCredit: CartPeerCredit) => void;
}) => {
  const { validateForms, cartCredits } = usePeerCreditsForm();

  const handleSubmit = () => {
    const { isValid } = validateForms();
    if (!isValid) return;

    onProceed(cartCredits);
  };

  const isDisabled = useMemo(() => {
    const peerTypeSelected =
      cartCredits.peerType === 'participants' || cartCredits.peerType === 'teams';
    const isCreditTo = cartCredits.credit_to?.length;
    const isCreditWriteIn = cartCredits.credit_write_in?.length;

    if (peerTypeSelected) {
      if (!isCreditTo && !isCreditWriteIn) {
        return true;
      }
    }
  }, [cartCredits]);

  return (
    <ButtonMantine onClick={handleSubmit} disabled={isDisabled}>
      Next
    </ButtonMantine>
  );
};

type PeerCreditSelectionForm = {
  peerType?: PeerTypes;
};

const PeerDonationCredit = ({
  onProceed,
  storeId,
  storeItemId,
  withCheckoutButtons,
}: {
  storeId: string;
  storeItemId: string;
  onProceed?: (cartCredit: CartPeerCredit) => void;
  withCheckoutButtons?: boolean;
}) => {
  const { data: campaignSettings } = useShowConsumerCampaignSettingsById({
    causeId: storeId,
    campaignId: storeItemId,
  });

  const { t: d } = useCrowdFundingTranslations(storeId);
  const { t } = useDonorTranslations({
    storeItemId,
    storeId,
  });

  const form = useForm<PeerCreditSelectionForm>({
    initialValues: {
      peerType: undefined,
    },
    validate: {
      peerType: (value) => (value === undefined ? 'Select credit option' : undefined),
    },
  });

  const setPeerCreditType = useCallback(
    (peerType?: { value?: string | number }) => {
      form.setFieldValue('peerType', peerType?.value as PeerCreditSelectionForm['peerType']);
    },
    [form]
  );

  const peerCreditType = useMemo(() => form.values.peerType, [form.values.peerType]);

  useFormListValidator({ form, id: 'peer_credit_selection' });
  const peerSelectionError = useMemo(() => Boolean(form.errors?.peerType), [form.errors]);

  if (!campaignSettings?.isParticipantsEnabled && !campaignSettings?.isTeamsEnabled) return null;

  return (
    <Stack gap="lg">
      <Stack>
        <Text fw={600}>Who should get credit for your {d('donation')}?</Text>
        <PeerCreditButtonGroup
          value={peerCreditType}
          onChange={setPeerCreditType}
          isParticipantsEnabled={campaignSettings?.isParticipantsEnabled}
          isTeamsEnabled={campaignSettings?.isTeamsEnabled}
          translations={{
            donate: d('Donate'),
            participant: t('participant'),
            team: t('team'),
          }}
        />
      </Stack>

      {peerCreditType && (
        <TransitionElement mounted={!!peerCreditType}>
          <PeerSelection
            type={peerCreditType}
            key={peerCreditType}
            storeId={storeId}
            storeItemId={storeItemId}
          />
        </TransitionElement>
      )}
      <Alert.Collapse in={peerSelectionError} title={form.errors.peerType} type="error" />
      {withCheckoutButtons && onProceed && <ProceedToDonationButton onProceed={onProceed} />}
    </Stack>
  );
};

export { PeerDonationCredit };
